const NAME_SUFFIXES = new Set([
  "jr",
  "jnr",
  "junior",
  "sr",
  "snr",
  "senior",
  "i",
  "ii",
  "iii",
  "iv",
  "v",
  "vi",
  "vii",
  "viii",
  "ix",
  "x",
  "1st",
  "2nd",
  "3rd",
  "4th",
  "5th",
  "6th",
  "7th",
  "8th",
  "9th",
  "10th",
  "2d",
  "3d",
  "esq",
  "phd",
  "md",
]);

const nameTokens = (fullName) =>
  (fullName || "")
    .trim()
    .replace(/,/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => token.replace(/\./g, ""));

export const splitBowlerName = (fullName) => {
  const raw = (fullName || "").trim();
  if (raw.includes(",")) {
    const comma = raw.indexOf(",");
    const lastTokens = nameTokens(raw.slice(0, comma));
    while (
      lastTokens.length > 1 &&
      NAME_SUFFIXES.has(lastTokens[lastTokens.length - 1].toLowerCase())
    ) {
      lastTokens.pop();
    }
    return {
      lastName: lastTokens.join(" ") || "",
      firstName: nameTokens(raw.slice(comma + 1)).join(" "),
    };
  }

  const tokens = nameTokens(fullName);
  while (
    tokens.length > 1 &&
    NAME_SUFFIXES.has(tokens[tokens.length - 1].toLowerCase())
  ) {
    tokens.pop();
  }

  return {
    lastName: tokens[tokens.length - 1] || "",
    firstName: tokens.slice(0, -1).join(" "),
  };
};

export const lastNameSortKey = (fullName) => {
  const { firstName, lastName } = splitBowlerName(fullName);
  return `${lastName.toLowerCase()}\t${firstName.toLowerCase()}`;
};

export const firstNameSortKey = (fullName) => {
  const { firstName, lastName } = splitBowlerName(fullName);
  return `${firstName.toLowerCase()}\t${lastName.toLowerCase()}`;
};

const compareText = (a, b) =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

export const compareByLastName = (a, b) =>
  compareText(lastNameSortKey(a?.bowlerName), lastNameSortKey(b?.bowlerName));

const averageSortValue = (bowler) => {
  if (bowler?.hasAverage === false) return null;
  if (bowler?.average === null || bowler?.average === undefined || bowler?.average === "") {
    return null;
  }
  const value = Number(bowler.average);
  return Number.isFinite(value) ? value : null;
};

export const compareBowlers = (a, b, sortBy = "lastName", sortOrder = "asc") => {
  const direction = sortOrder === "desc" ? -1 : 1;

  if (sortBy === "average") {
    const aAverage = averageSortValue(a);
    const bAverage = averageSortValue(b);
    if (aAverage === null && bAverage === null) {
      return compareByLastName(a, b);
    }
    if (aAverage === null) return 1;
    if (bAverage === null) return -1;
    if (aAverage !== bAverage) return (aAverage - bAverage) * direction;
    return compareByLastName(a, b);
  }

  const keyFor = sortBy === "firstName" ? firstNameSortKey : lastNameSortKey;
  const compared = compareText(keyFor(a?.bowlerName), keyFor(b?.bowlerName));
  if (compared !== 0) return compared * direction;
  return compareByLastName(a, b);
};

export const nameMatchesSearch = (fullName, query) => {
  const tokens = (query || "").trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return true;
  const name = (fullName || "").toLowerCase();
  return tokens.every((token) => name.includes(token));
};

export const sortLabel = (sortBy, sortOrder) => {
  const direction =
    sortBy === "average"
      ? sortOrder === "asc"
        ? "low to high"
        : "high to low"
      : sortOrder === "asc"
        ? "A to Z"
        : "Z to A";
  const field =
    sortBy === "firstName"
      ? "first name"
      : sortBy === "average"
        ? "average"
        : "last name";
  return `Sorted by ${field}, ${direction}.`;
};
